import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import useSEO from '../hooks/useSEO';

function GetStartedSedsi2026Page({ logo }) {
  useSEO({
    title: 'Get Started – SEDSI 2026',
    description: 'Connect with Veydra at SEDSI 2026. Request a demo, get presentation slides, and explore simulation tools.'
  });

  const [formData, setFormData] = useState({
    email: '',
    requestDemo: false,
    requestSlides: false,
    roles: []
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roleOptions = [
    { value: 'educator', label: 'Educator' },
    { value: 'executive', label: 'Executive' },
    { value: 'practitioner', label: 'Practitioner' },
    { value: 'student', label: 'Student' },
    { value: 'researcher', label: 'Researcher' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleRoleChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      roles: checked
        ? [...prev.roles, value]
        : prev.roles.filter(role => role !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'form-submissions-incremental'), {
        email: formData.email,
        requestDemo: formData.requestDemo,
        requestSlides: formData.requestSlides,
        roles: formData.roles,
        source: 'sedsi-2026-slides',
        page: '/sedsi2026',
        intent: 'sedsi_2026_slides',
        submittedAt: serverTimestamp()
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      <main className="max-w-4xl mx-auto px-8 py-16 md:py-24">
        {/* Conference Badge */}
        <div className="text-center mb-12">
          <span className="inline-block bg-gray-900 text-white text-xs font-medium uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
            SEDSI 2026 — Jacksonville, FL
          </span>
          <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-4 leading-tight tracking-tight">
            Download SEDSI 2026 Slides
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Interactive Simulations and Cutting-edge Innovations in the DASI track.
            Enter your details below to receive the presentation slides.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Column: Form */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8">
            {!submitted ? (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Get the Slides</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Fill out the form below and we'll send you the presentation slides.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Email */}
                  <div>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Email Address *"
                    />
                  </div>

                  {/* Interest Checkboxes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">I'm interested in:</label>
                    <div className="space-y-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="requestSlides"
                          checked={formData.requestSlides}
                          onChange={handleCheckboxChange}
                          className="w-4 h-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-gray-700">Send me the presentation slides</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="requestDemo"
                          checked={formData.requestDemo}
                          onChange={handleCheckboxChange}
                          className="w-4 h-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-gray-700">Request a demo of Veydra</span>
                      </label>
                    </div>
                  </div>

                  {/* Role Checkboxes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">I am a:</label>
                    <div className="space-y-2">
                      {roleOptions.map((role) => (
                        <label key={role.value} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            value={role.value}
                            checked={formData.roles.includes(role.value)}
                            onChange={handleRoleChange}
                            className="w-4 h-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                          />
                          <span className="ml-3 text-gray-700">{role.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gray-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Download Slides →'}
                  </button>
                </form>

                {error && (
                  <p className="text-xs text-red-600 mt-3 text-center">{error}</p>
                )}
                <p className="text-xs text-gray-500 mt-4 text-center">
                  By submitting, you agree to receive occasional updates from Veydra.
                </p>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Thank you!</h3>
                <p className="text-sm text-gray-600 mb-4">
                  We'll send the SEDSI 2026 presentation slides to your email shortly.
                </p>
                {formData.requestDemo && (
                  <p className="text-sm text-gray-600">
                    We'll also be in touch to schedule your demo.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Context */}
          <div>
            <h2 className="text-2xl font-medium text-gray-900 mb-4 tracking-tight">
              About the Presentation
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Presented at the <strong>SEDSI 2026 Conference</strong> in Jacksonville, FL as part of the
              DASI track on Interactive Simulations and Cutting-edge Innovations.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">February 18–20, 2026</h4>
                  <p className="text-sm text-gray-600">SEDSI Conference, Jacksonville, FL</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">Key Topics</h4>
                  <p className="text-sm text-gray-600">AI-augmented simulation, systems thinking, decision intelligence, interactive modeling</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">Track</h4>
                  <p className="text-sm text-gray-600">DASI — Decision Analytics and Systems Intelligence</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong className="text-gray-900">Veydra</strong> is a cloud-native platform for strategic simulation and decision intelligence.
                From supply chain to public policy, Veydra helps teams model complex systems, run scenarios, and make better decisions.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/educators"
                className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700"
              >
                For Educators
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700"
              >
                Contact Us
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default GetStartedSedsi2026Page;
